// components/OrderConfirmationModal.jsx
//
// Shown right after a user places an order (before they've paid).
// It shows:
//   1. Order confirmation with a UPI-ORD-{id} reference number
//   2. A "Proceed to WhatsApp" button that opens wa.me with order
//      details pre-filled (same trick as Checkout.jsx uses)
//   3. Your refund policy, so it's visible at the exact moment someone
//      is about to pay - reduces "I didn't know" disputes later
//   4. A close (×) button in the corner that dismisses the modal and
//      sends the user back to the homepage
//
// Uses Tailwind classes (matching the style you pasted). Make sure
// Tailwind is installed in the frontend - see note at bottom of this file.

import React from 'react';
import { useNavigate } from 'react-router-dom';

function OrderConfirmationModal({ order, whatsappNumber, onClose }) {
  const navigate = useNavigate();

  const whatsappMessage = encodeURIComponent(
    `Hi, I've paid for:\n` +
    `Product: ${order.productName}\n` +
    `Quantity: ${order.quantity}\n` +
    `Amount: ₹${order.totalPrice}\n` +
    `Order ID: UPI-ORD-${order._id}\n\n` +
    `Attaching my payment screenshot below.`
  );

  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const handleCloseAndGoHome = () => {
    onClose();       // let parent know modal is closed (e.g. reset checkout state)
    navigate('/');    // send user back to homepage
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">

        {/* Close button */}
        <button
          onClick={handleCloseAndGoHome}
          aria-label="Close and return to home"
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700 transition-colors"
        >
          &times;
        </button>

        <div className="p-6 space-y-4 text-center">
          <h3 className="text-2xl font-bold text-green-400">Order Initiated!</h3>
          <p className="text-gray-300">
            Your order <span className="font-mono">UPI-ORD-{order._id}</span> has been initiated with UPI payment.
          </p>
          <p className="text-gray-400 text-sm">
            Please complete the UPI payment and send the transaction screenshot via WhatsApp.
          </p>

          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            Proceed to WhatsApp
          </a>
        </div>

        {/* Refund policy - shown right at the point of payment */}
        <div className="p-6 pt-0 space-y-3 text-left text-sm text-gray-300 border-t border-gray-700 mt-2">
          <p>
            At NFXSELLERAASHU, customer satisfaction matters to us. Because our products are
            digital and service-based, refunds are only granted under the specific conditions below.
          </p>

          <h4 className="text-base font-bold text-green-400 pt-2">✅ Eligible for Refund</h4>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>Product Out of Stock</strong> — if what you ordered can't be delivered.</li>
            <li><strong>Technical or Product Issues</strong> — a verified issue on our end preventing proper use.</li>
            <li><strong>Order Cancelled by Us</strong> — cancelled due to an internal error on our side.</li>
          </ul>
          <p className="pl-2 text-gray-400">
            In these cases, a full refund is processed within <strong>5–7 business days</strong>.
          </p>

          <h4 className="text-base font-bold text-red-400 pt-2">❌ Not Eligible for Refund</h4>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>Customer Mistake</strong> — wrong details, wrong product, or other input errors.</li>
            <li><strong>Active or Delivered Products</strong> — once activated/delivered, it's non-refundable.</li>
            <li><strong>Ignoring Disclaimers</strong> — refund requests after not reading product descriptions.</li>
          </ul>

          <h4 className="text-base font-bold text-yellow-400 pt-2">⚠️ Important Note</h4>
          <p>
            Please read the product description and disclaimer carefully before purchase. By
            placing an order, you agree to this Refund Policy.
          </p>

          <p className="pt-2">
            Questions about a refund? Contact us at{' '}
            <span className="text-green-400 font-bold">tempaashu8252@gmail.com</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default OrderConfirmationModal;

/*
  SETUP NOTE - Tailwind CSS
  If your frontend doesn't have Tailwind yet, install it with:

    npm install -D tailwindcss postcss autoprefixer
    npx tailwindcss init -p

  Then in tailwind.config.js, set the content array to scan all JS/JSX
  files inside the src folder (see Tailwind's official docs for the
  exact glob pattern - avoiding it here since certain character
  sequences inside a JS comment can accidentally close the comment early).

  And add these three lines to your main CSS file (e.g. src/index.css):
    @tailwind base;
    @tailwind components;
    @tailwind utilities;
*/
